import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import en from '../locales/en/common.json';
import esAR from '../locales/es-AR/common.json';
import esES from '../locales/es-ES/common.json';

export type SupportedLang = 'en' | 'es-AR' | 'es-ES';

const dictionaries: Record<SupportedLang, any> = {
  en,
  'es-AR': esAR,
  'es-ES': esES,
};

function getBrowserLang(): SupportedLang {
  const ls = typeof window !== 'undefined' ? (localStorage.getItem('lang') as SupportedLang | null) : null;
  if (ls && (ls === 'en' || ls === 'es-AR' || ls === 'es-ES')) return ls;
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  if (nav.toLowerCase().startsWith('es-es')) return 'es-ES';
  if (nav.toLowerCase().startsWith('es-ar')) return 'es-AR';
  if (nav.toLowerCase().startsWith('es')) return 'es-ES';
  return 'en';
}

function resolvePath(obj: any, path: string): any {
  return path.split('.').reduce((acc: any, key: string) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
}

interface I18nContextShape {
  lang: SupportedLang;
  t: (key: string) => string;
  setLang: (lang: SupportedLang) => void;
}

const I18nContext = createContext<I18nContextShape | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<SupportedLang>(getBrowserLang());

  const setLang = useCallback((l: SupportedLang) => {
    setLangState(l);
    try { localStorage.setItem('lang', l); } catch {}
  }, []);

  const dict = dictionaries[lang] || dictionaries.en;

  const t = useCallback((key: string) => {
    const v = resolvePath(dict, key);
    if (typeof v === 'string') return v;
    // If a full sentence was passed or key missing, just return the original input
    return key;
  }, [dict]);

  const value = useMemo(() => ({ lang, t, setLang }), [lang, t, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
